import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { bootTelemetry, feature, featureForPath, setTelemetryOrg } from '../../services/telemetry';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import Rail from './Rail';
import SecondaryRail from './SecondaryRail';
import SlideoutPanel from './SlideoutPanel';
import { useAppStore } from '../../store/useAppStore';
import DashboardPage from '../../pages/DashboardPage';
import ProjectPage from '../../pages/ProjectPage';
import KanbanPage from '../../pages/KanbanPage';
import ListPage from '../../pages/ListPage';
import FundingPage from '../../pages/FundingPage';
import FunderPage from '../../pages/FunderPage';
import AdminResourcesPage from '../../pages/AdminResourcesPage';
import AdminUsersPage from '../../pages/AdminUsersPage';
import AdminApiTokensPage from '../../pages/AdminApiTokensPage';
import AdminAccessPage from '../../pages/AdminAccessPage';
import AdminOutboxPage from '../../pages/AdminOutboxPage';
import WorkflowsPage from '../../pages/WorkflowsPage';
import LeadsPage from '../../pages/LeadsPage';
import OpportunitiesPage from '../../pages/OpportunitiesPage';
import ClientsPage from '../../pages/ClientsPage';
import ItsmPage from '../../pages/ItsmPage';
import TrainingPage from '../../pages/TrainingPage';
import TrainingCampaignPage from '../../pages/TrainingCampaignPage';
import TrainingDashboardPage from '../../pages/TrainingDashboardPage';
import TrainingProfilePage from '../../pages/TrainingProfilePage';
import TrainingWorkPage from '../../pages/TrainingWorkPage';
import TrainingNewPage from '../../pages/TrainingNewPage';
import TrainingCampaignNewPage from '../../pages/TrainingCampaignNewPage';
import TrainingDecisionsPage from '../../pages/TrainingDecisionsPage';
import TrainingAccessPage from '../../pages/TrainingAccessPage';
import TrainingRelationshipsPage from '../../pages/TrainingRelationshipsPage';
import WaysPage from '../../pages/WaysPage';
import WaysDesignStandardPage from '../../pages/WaysDesignStandardPage';
import ApprovalsPage from '../../pages/ApprovalsPage';
import CmdbPage from '../../pages/CmdbPage';
import MyQueuePage from '../../pages/MyQueuePage';
import CatalogPage from '../../pages/CatalogPage';
import QuotesPage from '../../pages/QuotesPage';
import InvoicesPage from '../../pages/InvoicesPage';
import ContractsPage from '../../pages/ContractsPage';
import GanttPage from '../../pages/GanttPage';
import WsjfPage from '../../pages/WsjfPage';
import PortfolioPage from '../../pages/PortfolioPage';
import IdeationPage from '../../pages/IdeationPage';
import ReleaseNotesPage from '../../pages/ReleaseNotesPage';
import ReleasesPage from '../../pages/ReleasesPage';
import ItemRedirectPage from '../../pages/ItemRedirectPage';
import WorkItemPathResolver from '../../pages/WorkItemPathResolver';
import WorkItemDetailSlideout from '../workitems/WorkItemDetailSlideout';
import {
  SettingsPage, OrgAdminPage, ProgramIncrementsPage, TasksPage,
} from '../../pages/PanelPages';
import ErrorBoundary from '../common/ErrorBoundary';

export default function AppShell() {
  const { darkMode } = useAppStore();
  const location = useLocation();
  const selectedOrgId = useOrgHierarchyStore((st) => st.selectedOrgId);

  // Usage telemetry (hs-pmo/ep20#1): dormant unless the server enables it; never in the way.
  useEffect(() => { void bootTelemetry(); }, []);
  useEffect(() => { feature(featureForPath(location.pathname)); }, [location.pathname]);
  useEffect(() => { setTelemetryOrg(selectedOrgId); }, [selectedOrgId]);

  // The theme lives on <html> as well as on this shell. Dialogs, slideouts and menus that portal
  // to <body> (every Headless UI Dialog does) sit outside this div, so with `dark` only here their
  // `dark:` classes never applied and they rendered light in dark mode (PO46-1649: the Ways
  // Channels slideout stayed white). On <html> every surface in the document takes the theme.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  return (
    <div className={`flex h-screen w-screen overflow-hidden ${darkMode ? 'dark bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Left Rail — primary groups, then the selected group's destinations */}
      <Rail />
      <SecondaryRail />

      {/* Slideout Panel */}
      <SlideoutPanel />

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
        {/* A page crash must not unmount the rails and leave a blank screen.
            Keyed on pathname so navigating away clears a caught error. */}
        <ErrorBoundary key={location.pathname} area="page">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/project/:projectId" element={<ProjectPage />} />
          <Route path="/project/:projectId/board" element={<KanbanPage />} />
          <Route path="/project/:projectId/list" element={<ListPage />} />
          <Route path="/board" element={<KanbanPage />} />
          <Route path="/list" element={<ListPage />} />
          <Route path="/funding" element={<FundingPage />} />
          <Route path="/funding/:fundingId" element={<FundingPage />} />
          <Route path="/funders" element={<FunderPage />} />
          <Route path="/funder/:funderId" element={<FunderPage />} />
          <Route path="/admin/resources" element={<AdminResourcesPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/api-tokens" element={<AdminApiTokensPage />} />
          <Route path="/admin/access" element={<AdminAccessPage />} />
          <Route path="/admin/outbox" element={<AdminOutboxPage />} />
          <Route path="/admin/workflows" element={<WorkflowsPage />} />
          {/* Destinations, not pickers — see PanelPages. Rendering these
              in a slideout left an unrelated page beside them. */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin/organizations" element={<OrgAdminPage />} />
          <Route path="/admin/program-increments" element={<ProgramIncrementsPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/crm/leads" element={<LeadsPage />} />
          <Route path="/crm/opportunities" element={<OpportunitiesPage />} />
          <Route path="/crm/clients" element={<ClientsPage />} />
          <Route path="/itsm" element={<ItsmPage />} />
          <Route path="/my-queue" element={<MyQueuePage />} />
          <Route path="/training" element={<TrainingPage />} />
          <Route path="/training/new" element={<TrainingNewPage />} />
          <Route path="/training/:programId" element={<TrainingPage />} />
          <Route path="/training/:programId/decisions" element={<TrainingDecisionsPage />} />
          <Route path="/training/:programId/campaigns/new" element={<TrainingCampaignNewPage />} />
          <Route path="/training/:programId/campaigns/:campaignId" element={<TrainingCampaignPage />} />
          <Route path="/training/:programId/work/:view" element={<TrainingWorkPage />} />
          <Route path="/training/:programId/profile" element={<TrainingProfilePage />} />
          <Route path="/training/:programId/access" element={<TrainingAccessPage />} />
          <Route path="/training/:programId/relationships" element={<TrainingRelationshipsPage />} />
          <Route path="/training/:programId/:board" element={<TrainingDashboardPage />} />
          <Route path="/approvals" element={<ApprovalsPage />} />
          <Route path="/ways" element={<WaysPage />} />
          <Route path="/ways/design-standard" element={<WaysDesignStandardPage />} />
          <Route path="/cmdb" element={<CmdbPage />} />
          <Route path="/crm/catalog" element={<CatalogPage />} />
          <Route path="/crm/quotes" element={<QuotesPage />} />
          <Route path="/crm/invoices" element={<InvoicesPage />} />
          <Route path="/crm/contracts" element={<ContractsPage />} />
          <Route path="/gantt" element={<GanttPage />} />
          <Route path="/project/:projectId/gantt" element={<GanttPage />} />
          <Route path="/priorities" element={<WsjfPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/ideation" element={<IdeationPage />} />
          <Route path="/project/:projectId/ideation" element={<IdeationPage />} />
          <Route path="/releases" element={<ReleasesPage />} />
          {/* Release notes reading view — deep-linkable and shareable */}
          <Route path="/release-notes" element={<ReleaseNotesPage />} />
          <Route path="/release-notes/:releaseId" element={<ReleaseNotesPage />} />
          {/* Stable permalink to any work item by internal _id */}
          <Route path="/item/:itemId" element={<ItemRedirectPage />} />
          {/* Human-readable full-path link, e.g. /PO-3/EP-1/FE-1/TS-1 (kept last:
              only matches paths no named route claimed) */}
          <Route path="/:projectRef/*" element={<WorkItemPathResolver />} />
        </Routes>
        </ErrorBoundary>
      </main>

      {/* Work Item Detail Slideout (right side) */}
      <WorkItemDetailSlideout />
    </div>
  );
}
